/**
 * 统一表态表 `jerry_ai_verdict` 的前端形状（表态设计 阶段二）。
 *
 * 承接的是**没有条目行可挂的整段文字产出**——对话回答、尽调子报告、会议摘要
 * 条目。已经一条结论一行的产出（审查发现、起草改动提案）在自己那一行上表态，
 * 类型见 `types/aiReview.ts`，不进本表。
 *
 * 三值语义与否决分类词表两条路径共用，因此表态值直接复用 `AiFindingProcessed`，
 * 不另立一套字面量——跨场景统计要把两边的 `rejected` 直接并起来算。
 */

import type { AiFindingProcessed, AiVerdictReasonCode } from "./aiReview";

/** 表态挂载的宿主对象类型（后端 `AiVerdictTargetKind`）。 */
export type AiVerdictTargetKind =
  | "conversation_message"
  | "engine_message"
  | "dd_sub_report"
  | "meeting_summary_item"
  | "scheduled_run_item";

/**
 * 统一写入端点 POST /ai-verdicts 的表态值（波次 2 冻结契约：agree/disagree）。
 *
 * 与行内三态 `AiFindingProcessed`（unprocessed/accepted/rejected）是两套词表：
 * 统一表态表没有墓碑语义，「撤销」在本端点不存在，改判即再写一条新行。
 */
export type AiVerdictVote = "agree" | "disagree";

/** Chinese display labels for the unified-verdict vote values. */
export const AI_VERDICT_VOTE_LABELS: Record<AiVerdictVote, string> = {
  agree: "认同",
  disagree: "不认同",
};

/** Selectable vote options. */
export const AI_VERDICT_VOTE_OPTIONS: { value: AiVerdictVote; label: string }[] = (
  Object.entries(AI_VERDICT_VOTE_LABELS) as [AiVerdictVote, string][]
).map(([value, label]) => ({ value, label }));

/**
 * POST /ai-verdicts 请求体（波次 2 契约）。`reason` 为自由文本理由，
 * `category` 取七项否决分类词表（`AiVerdictReasonCode`），两者均仅在
 * `verdict === "disagree"` 时有意义且都可选。
 */
export interface AiVerdictCreatePayload {
  targetKind: AiVerdictTargetKind;
  targetId: string;
  /** 对象内定位（期产物为步骤 key）；整条表态时为空字符串。 */
  targetRef: string;
  verdict: AiVerdictVote;
  reason?: string | null;
  category?: AiVerdictReasonCode | null;
}

/** 表态发生在哪类工作场景（后端 `AiVerdictScene`），落库时冗余记录。 */
export type AiVerdictScene =
  | "conversation"
  | "doc_review"
  | "draft"
  | "due_diligence"
  | "meeting";

/**
 * 一条表态记录。读取侧一律是「每个宿主对象上本人的最新一条」——append-only 的
 * 历史行不透出到界面（界面要显示的是当前表态，不是变更史）。
 *
 * `verdict === "unprocessed"` 是**撤销表态**的墓碑行：本表没有默认行，未表态的
 * 对象在表里一行都没有，因此该值只会由律师点过又反悔产生。界面把它与"从没表过
 * 态"同样呈现为未表态。
 */
export interface AiVerdict {
  id: string;
  targetKind: AiVerdictTargetKind;
  /** 宿主标识。对话消息是 `jerry_messages.id`；引擎侧消息不是 UUID 形态，
   *  故统一为字符串。 */
  targetId: string;
  /** 对象内定位（会议摘要的数组下标等）。整条表态时为空字符串。 */
  targetRef: string;
  /** 承载宿主的容器 id（对话消息填会话 id）。宿主没有容器概念时为 null。 */
  hostId: string | null;
  verdict: AiFindingProcessed;
  reasonCode: AiVerdictReasonCode | null;
  reasonText: string | null;
  /** 律师选中的那一句，纯文本快照；不建位置锚点、不承诺点回原文。 */
  excerpt: string | null;
  /** 表态时该结论的正文快照（服务端截断），供事后核对当时否掉的是哪段话。 */
  contentSnapshot: string | null;
  scene: AiVerdictScene;
  skillId: string | null;
  answeredByAgent: string | null;
  modelId: string | null;
  hasCitation: boolean;
  createdBy: string;
  createdAt: string;
}
